import { Controller } from "@hotwired/stimulus"
import { Timeline, replay, setOpen } from "~/lib/motion"

// Hero admin mockup: the create button walks through the setup steps, then adds
// the new item to the table. Clicking again resets the demo. The idle and
// busy button labels come from data-admin-setup-idle-value / busy-value.
export default class extends Controller {
  static targets = ["cta", "label", "panel", "step", "rows", "rowTemplate", "count", "tile"]
  static values = { count: Number, idle: { type: String, default: "Create" }, busy: { type: String, default: "Creating…" } }

  connect() {
    this.timeline = new Timeline()
    this.done = false
  }

  disconnect() {
    this.timeline.cancel()
  }

  toggle() {
    this.done ? this.reset() : this.create()
  }

  reset() {
    this.timeline.cancel()
    setOpen(this.panelTarget, false)
    this.clear()
  }

  create() {
    this.timeline.cancel()
    this.clear()
    this.ctaTarget.classList.remove("is-hinting")
    this.labelTarget.textContent = this.busyValue
    setOpen(this.panelTarget, true)

    this.timeline.run(async (wait) => {
      for (const step of this.stepTargets) {
        this.setStep(step, "running")
        await wait(850)
        this.setStep(step, "done")
        await wait(250)
      }
      await wait(500)
      setOpen(this.panelTarget, false)
      this.rowsTarget.prepend(this.rowTemplateTarget.content.firstElementChild.cloneNode(true))
      this.countTarget.textContent = this.countValue + 1
      replay(this.tileTarget, "is-bump")
      replay(this.tileTarget, "is-flash")
      this.labelTarget.textContent = "Reset demo"
      this.done = true
    })
  }

  clear() {
    this.done = false
    this.stepTargets.forEach((step) => this.setStep(step, "pending"))
    this.rowsTarget.querySelector(".is-new")?.remove()
    this.countTarget.textContent = this.countValue
    this.labelTarget.textContent = this.idleValue
  }

  setStep(step, state) {
    const num = step.querySelector(".mock-step__num")
    const detail = step.querySelector("small")
    const status = step.querySelector(".mock-step__status")
    step.classList.toggle("is-pending", state === "pending")
    num.classList.toggle("is-done", state === "done")

    if (state === "pending") {
      num.textContent = this.stepTargets.indexOf(step) + 1
      detail.textContent = "Waiting"
      status.replaceChildren()
    } else if (state === "running") {
      num.replaceChildren(element("span", "mock-spinner"))
      status.replaceChildren(element("span", "mock-badge mock-badge--info", "In progress"))
    } else {
      num.innerHTML = '<svg class="icon icon--sm icon--bold"><use href="#icon-check"/></svg>'
      detail.textContent = detail.dataset.done
      status.replaceChildren(element("span", "mock-badge mock-badge--success", "Done"))
    }
  }
}

function element(tag, className, text = "") {
  const node = document.createElement(tag)
  node.className = className
  node.textContent = text
  return node
}
